/**
 * Pure view model of the venues screens (task T.C4a): the URL state of the
 * roster, the drill-down paths, the quota grid and the failure copy.
 *
 * Deliberately framework-free AND bundler-free — the `unit` Playwright project
 * imports it in plain Node, so nothing here may touch `import.meta.env`
 * (`./client`) or the extensionless ESM build of `@cb-manager/shared`. The
 * status vocabulary it reuses lives in `../dashboard/status` for the same
 * reason: six statuses, one list, one label map — declared once for the whole
 * SPA.
 */

import type {
  QuotaDto,
  VenueCourseFormat,
  VenueDto,
  VenueKind,
  VenuePatch,
  VenueStatusCountMap,
} from '../../api/venues';

import { MISSING, formatCount } from '../dashboard/format';
import { DASHBOARD_STATUSES, sumByStatus } from '../dashboard/status';
import {
  DEPARTMENT_LABEL,
  parseDepartmentBucket,
  venueMatchesDepartment,
  type DepartmentBucket,
  type DepartmentSource,
  type VenueManagersLike,
} from '../../departments';
import { managerDisplayLabel } from './venue-links-model';

// ---------------------------------------------------------------------------
// URL state
// ---------------------------------------------------------------------------

export const VENUES_PATH = '/venues';

/** Query params the venues screens own (lib/query-params reads/writes them). */
export const PARAM_ID = 'id';
export const PARAM_REGION = 'region';
export const PARAM_Q = 'q';
export const PARAM_PAGE = 'page';
/** Wave 6 (owner) — the roster's «Площадка» type-ahead commits the venue's id. */
export const PARAM_VENUE_ID = 'venueId';
/** ADDITIVE (wave 10, A5/T.Z18) — the roster's «Тип» filter: the reporting kind. */
export const PARAM_KIND = 'kind';
/** ADDITIVE (T.Z42, v1.8.7) — the roster's «Отдел» filter: the managers' department. */
export const PARAM_DEPARTMENT = 'department';

export interface VenuesListParams {
  region?: string;
  q?: string;
  venueId?: number;
  /** The reporting kind the roster is narrowed to; absent = «Все» (both kinds). */
  kind?: VenueKind;
  /**
   * ADDITIVE (T.Z42, v1.8.7) — the department bucket the roster is narrowed to:
   * `OP` | `OS` | `NONE` | `MIXED`; absent = every venue. The narrowing is the
   * BROWSER's (see {@link toListQuery}), so this is URL state, not a query key.
   */
  department?: DepartmentBucket;
  page: number;
}

/** `?id=12` opens the venue card INSTEAD of the roster — see the router note in VenuesPage. */
export function parseVenueIdParam(value: string | undefined): number | null {
  const text = value?.trim() ?? '';
  // Digits only: the api's own path parser (#7) refuses `1.5` and `+12`, and a
  // hand-edited URL must not ask for a venue those would never return.
  return /^\d+$/.test(text) && Number(text) > 0 ? Number(text) : null;
}

function trimOrUndefined(value: string | undefined): string | undefined {
  const text = value?.trim();
  return text ? text : undefined;
}

/**
 * `REGISTRY | PARTNER_ONLINE` or nothing — a hand-edited `?kind=онлайн` is a
 * fallback to «Все», never a request the api would have to refuse.
 */
export function parseKindParam(value: string | undefined): VenueKind | undefined {
  return value === 'REGISTRY' || value === 'PARTNER_ONLINE' ? value : undefined;
}

/**
 * The roster state of a URL. An out-of-range page (`?page=abc`, `?page=0`) is a
 * hand-edited URL, not an error — it falls back to the first page.
 */
export function parseVenuesParams(params: Record<string, string>): VenuesListParams {
  const page = Number.parseInt(params[PARAM_PAGE] ?? '', 10);
  const venueId = Number.parseInt(params[PARAM_VENUE_ID] ?? '', 10);
  return {
    region: trimOrUndefined(params[PARAM_REGION]),
    q: trimOrUndefined(params[PARAM_Q]),
    venueId: Number.isFinite(venueId) && venueId > 0 ? venueId : undefined,
    kind: parseKindParam(params[PARAM_KIND]),
    department: parseDepartmentBucket(params[PARAM_DEPARTMENT]),
    page: Number.isFinite(page) && page > 0 ? page : 1,
  };
}

/** The query-string half of a roster URL — filters only, defaults never leak. */
function listSearch(params: VenuesListParams): string {
  const search = new URLSearchParams();
  if (params.region) search.set(PARAM_REGION, params.region);
  if (params.q) search.set(PARAM_Q, params.q);
  if (params.venueId !== undefined) search.set(PARAM_VENUE_ID, String(params.venueId));
  if (params.kind !== undefined) search.set(PARAM_KIND, params.kind);
  if (params.department !== undefined) search.set(PARAM_DEPARTMENT, params.department);
  if (params.page > 1) search.set(PARAM_PAGE, String(params.page));
  return search.toString();
}

/** The roster URL of a filter state (`/venues?region=…`), default state = bare path. */
export function venuesListPath(params: VenuesListParams): string {
  const search = listSearch(params);
  return search ? `${VENUES_PATH}?${search}` : VENUES_PATH;
}

/**
 * The card URL of one venue. The roster filters travel along so that the card's
 * "back to the roster" link restores the exact view the user drilled out of —
 * the same convention the dashboard's drill-down uses for its own filter.
 */
export function venueCardPath(id: number, params: VenuesListParams): string {
  const search = listSearch(params);
  const prefix = search ? `${search}&` : '';
  return `${VENUES_PATH}?${prefix}${PARAM_ID}=${id}`;
}

/** The card's query args, of which `id` is the only mandatory one. */
export function cardParams(params: Record<string, string>): { id: number | null; list: VenuesListParams } {
  const list = parseVenuesParams(params);
  return { id: parseVenueIdParam(params[PARAM_ID]), list };
}

/**
 * The roster query the api receives (#6). The page default is dropped here too,
 * so the wire request equals the URL the user sees. `50` is the contract's
 * `pageSize` default (api/venues carries the named constant; this module cannot
 * import it without dragging `import.meta.env` into the plain-Node unit run).
 */
export const DEFAULT_PAGE_SIZE = 50;

/**
 * The page size the «Тип» filter asks for: #6's own ceiling (`VENUE_DIRECTORY_PAGE_SIZE`
 * in `api/venues` — the api caps `pageSize` at 200), spelled here as a literal because
 * this module stays import-free for the plain-Node unit run. 189 venues fit one page
 * of it, which is what makes the client-side kind filter honest — see {@link toListQuery}.
 */
export const KIND_FILTER_PAGE_SIZE = 200;

/**
 * The #6 query the roster sends.
 *
 * A5/T.Z18: the «Тип» filter has NO query parameter on #6 (the frozen contract
 * takes `region`/`q`/`venueId`/`page`/`pageSize` only), so the narrowing happens in
 * the browser over {@link venuesOfKind}. To keep that honest — a filter over the
 * CURRENT PAGE would silently drop the partner branches that live on another page —
 * asking for a kind also raises the page size to #6's 200 ceiling and restarts at
 * page 1, which is the whole roster in one answer. The filtered view therefore shows
 * every venue of the kind, and its counter is the true count.
 */
export function toListQuery(params: VenuesListParams, pageSize: number = DEFAULT_PAGE_SIZE) {
  if (params.kind !== undefined || params.department !== undefined) {
    return {
      region: params.region,
      q: params.q,
      venueId: params.venueId,
      page: undefined,
      pageSize: KIND_FILTER_PAGE_SIZE,
    };
  }
  return {
    region: params.region,
    q: params.q,
    venueId: params.venueId,
    page: params.page > 1 ? params.page : undefined,
    pageSize: pageSize === DEFAULT_PAGE_SIZE ? undefined : pageSize,
  };
}

/**
 * ADDITIVE (T.Z42) — the rows the roster prints once the «Отдел» filter is applied.
 * The bucket comes from the managers' departments (see `../departments` for the
 * join), so the CALLER supplies the directory map; this module stays shape-only.
 */
export function venuesOfDepartment<Row extends VenueManagersLike>(
  rows: ReadonlyArray<Row>,
  usersById: ReadonlyMap<number, DepartmentSource> | undefined,
  bucket: DepartmentBucket | undefined,
): Row[] {
  if (bucket === undefined) return [...rows];
  return rows.filter((row) => venueMatchesDepartment(row, usersById, bucket));
}

/**
 * The rows the roster prints once the «Тип» filter is applied. A row without a
 * `kind` (an older api, a hand-built stub) IS a registry venue — the same
 * degradation {@link venueKindLabel} makes — so it survives an «Офлайн» filter and
 * disappears from an «Онлайн-филиалы» one.
 */
export function venuesOfKind<Row extends Pick<VenueDto, 'kind'>>(
  rows: ReadonlyArray<Row>,
  kind: VenueKind | undefined,
): Row[] {
  if (kind === undefined) return [...rows];
  return rows.filter((row) => (row.kind ?? 'REGISTRY') === kind);
}

/** The two counts the «Тип» options print, over the whole directory (#6, one read). */
export interface VenueKindCounts {
  registry: number;
  partnerOnline: number;
}

/**
 * The directory → the «Тип» counts. The directory is the one read the roster page
 * already makes for the «Площадка» type-ahead, it covers the whole roster (one page
 * of 200), and it carries `kind` on every entry — so the options can show how many
 * venues each choice selects without a second request. An empty/failed directory
 * yields `null` and the options print their bare labels.
 */
export function venueKindCounts(
  directory: ReadonlyArray<Pick<VenueDirectoryLikeEntry, 'kind'>> | null | undefined,
): VenueKindCounts | null {
  if (!directory || directory.length === 0) return null;
  let registry = 0;
  let partnerOnline = 0;
  for (const entry of directory) {
    if ((entry.kind ?? 'REGISTRY') === 'PARTNER_ONLINE') partnerOnline += 1;
    else registry += 1;
  }
  return { registry, partnerOnline };
}

/** Structural slice of a directory entry, so plain Node can run the counter. */
export interface VenueDirectoryLikeEntry {
  kind?: VenueKind;
}

/**
 * The «Тип» options, counts included when the directory answered: «Все (189)» /
 * «Офлайн (174)» / «Онлайн-филиалы (15)». The labels are the owner's words for the
 * two kinds; each option's tooltip spells the badge the row carries, so «Офлайн»
 * and «РЕЕСТР» can never read as two different things.
 */
export function venueKindFilterOptions(
  counts: VenueKindCounts | null | undefined,
): Array<{ value: string; label: string; title: string }> {
  const all = counts ? `Все (${counts.registry + counts.partnerOnline})` : 'Все';
  const registry = counts ? `Офлайн (${counts.registry})` : 'Офлайн';
  const partner = counts ? `Онлайн-филиалы (${counts.partnerOnline})` : 'Онлайн-филиалы';
  return [
    { value: '', label: all, title: 'Реестровые площадки и филиалы партнёров' },
    { value: 'REGISTRY', label: registry, title: 'Реестровые площадки офлайн-набора — бейдж «РЕЕСТР»' },
    {
      value: 'PARTNER_ONLINE',
      label: partner,
      title: 'Филиалы партнёров онлайн-набора — бейдж «ПАРТНЕР-ОНЛАЙН»',
    },
  ];
}

/** `meta.total / meta.pageSize` rounded up — the pager's ceiling. */
export function totalPages(total: number, pageSize: number): number {
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(pageSize) || pageSize <= 0) return 1;
  return Math.ceil(total / pageSize);
}

/**
 * Russian plural forms — `1 площадка / 2 площадки / 5 площадок`, with the
 * 11–14 exception (teens take the genitive plural). A count that is not a sane
 * number is `0`.
 */
export function pluralRu(count: number | null | undefined, forms: readonly [string, string, string]): string {
  const value =
    typeof count === 'number' && Number.isFinite(count) ? Math.abs(Math.round(count)) : 0;
  const mod100 = value % 100;
  if (mod100 >= 11 && mod100 <= 14) return forms[2];
  switch (value % 10) {
    case 1:
      return forms[0];
    case 2:
    case 3:
    case 4:
      return forms[1];
    default:
      return forms[2];
  }
}

const VENUE_FORMS: readonly [string, string, string] = ['площадка', 'площадки', 'площадок'];

/** The roster counter, pluralised — «1 площадка», «60 площадок». */
export function venueCountLabel(count: number | null | undefined): string {
  return `${formatCount(count)} ${pluralRu(count, VENUE_FORMS)}`;
}

// ---------------------------------------------------------------------------
// Drill-down
// ---------------------------------------------------------------------------

/**
 * `/applications?venueId=<id>` — the same path the dashboard drills through
 * (reused verbatim, so the two entry points can never disagree about the
 * parameter name T.C3 reads). `region` rides along when the roster is scoped.
 */
export function applicationsDrillDown(venueId: number, region?: string): string {
  const search = new URLSearchParams();
  search.set('venueId', String(venueId));
  if (typeof region === 'string' && region.trim() !== '') search.set('region', region);
  return `/applications?${search.toString()}`;
}

/**
 * `/teachers?venueId=<id>` — the teacher roster pre-filtered to one venue (T.R10). The
 * param name is the teachers page's own (`teachers-model` reads `venueId`), spelled here
 * so the venue card and any other caller link through ONE builder. `teacherId` opens the
 * T.R7 drawer on a specific row of that same filtered roster.
 */
export function teachersRosterPath(venueId: number, teacherId?: number): string {
  const teacher = teacherId !== undefined && Number.isFinite(teacherId) && teacherId > 0 ? teacherId : undefined;
  return teacher === undefined
    ? `/teachers?venueId=${venueId}`
    : `/teachers?venueId=${venueId}&teacherId=${teacher}`;
}

/**
 * `/venues/:venueId/prefill` (T.Z27) — the venue card's «Онлайн-набор» page, the SAME
 * cabinet online block scoped to ONE venue for a МЕНЕДЖЕР (or an АДМИН) linked to a
 * flagged venue. A PATH route on purpose, unlike the card's `?id=`: the surface has no
 * roster state to carry back, and a shareable link that names the venue outright is
 * what a manager forwards to a colleague. The router owns the segment name
 * (`venueId`); this builder is the one place that spells it.
 */
export function venuePrefillPath(venueId: number): string {
  return `/venues/${venueId}/prefill`;
}

/** How many ФИО the venue card prints before the «ещё N» link takes over (T.R10). */
export const TEACHERS_PREVIEW_LIMIT = 5;

// ---------------------------------------------------------------------------
// Roster rows
// ---------------------------------------------------------------------------

/**
 * A #6 row. `appStatusCounts` is NOT in the frozen `VenueDto` — it is a #7
 * aggregate — so it is optional here, and the roster renders the absence
 * marker when a row arrives without it (today: always). `curatorName` is the
 * same story one task early: T.R8 owns the curator attribute, the column is
 * already on the roster, so it reads the field when it is there and prints the
 * absence marker when it is not.
 */
export type VenueRow = VenueDto & {
  appStatusCounts?: Partial<VenueStatusCountMap> | null;
  curatorName?: string | null;
};

/** The roster's «Куратор» cell — present from T.R8 on, the marker before it. */
export function curatorNameLabel(venue: VenueRow | null | undefined): string {
  return sheetCellLabel(venue?.curatorName);
}

// ---------------------------------------------------------------------------
// ADDITIVE wave 9 (v1.8.0, T.Y9) — the venue's reporting kind and partner block
// ---------------------------------------------------------------------------

/** «РЕЕСТР» / «ПАРТНЕР-ОНЛАЙН» — the badge on a roster row and on the venue card. */
export function venueKindLabel(kind: VenueKind | undefined): string {
  return kind === 'PARTNER_ONLINE' ? 'ПАРТНЕР-ОНЛАЙН' : 'РЕЕСТР';
}

/** True for a partner filial — the venue whose plan is the partner's SHARED pool. */
export function isPartnerVenue(venue: Pick<VenueDto, 'kind'>): boolean {
  return venue.kind === 'PARTNER_ONLINE';
}

/** «АКАДЕМИЯ ТОП» / the partner's name, or the fallback when the read is silent. */
export function partnerLabel(venue: Pick<VenueDto, 'partnerName' | 'partnerId'>): string {
  if (venue.partnerName !== null && venue.partnerName !== undefined && venue.partnerName !== '') {
    return venue.partnerName;
  }
  return venue.partnerId === null || venue.partnerId === undefined ? '—' : `Партнёр #${venue.partnerId}`;
}

/** The plan a partner branch reports: the SHARED pool, never the venue's own (deliberate 0). */
export function partnerPoolOf(venue: Pick<VenueDto, 'partnerPlanOnline'>): number {
  return typeof venue.partnerPlanOnline === 'number' && Number.isFinite(venue.partnerPlanOnline)
    ? venue.partnerPlanOnline
    : 0;
}

/**
 * The #8 fragment of the D3 flag: `{allowCuratorChildren}` when the checkbox
 * moved, `null` when it did not — the card's one PATCH carries only the keys
 * that changed, and an unchanged flag must not read as a change in the audit.
 * An older api that omits the field reads as `false`, which every pre-wave-9
 * venue was.
 */
export function allowCuratorChildrenPatch(
  venue: Pick<VenueDto, 'allowCuratorChildren'>,
  next: boolean,
): VenuePatch | null {
  return (venue.allowCuratorChildren ?? false) === next ? null : { allowCuratorChildren: next };
}

/**
 * ADDITIVE (v1.9.15, T.Z94) — the #8 fragment of the venue's OWN department, the
 * «Отдел» select of the card's edit form: `''` is the clear («не задано» → the api
 * stores `null`), a stored value absent from the payload (an older api) reads the
 * same. The same arithmetic as {@link allowCuratorChildrenPatch}: only a MOVED select
 * travels, so an untouched form never writes a no-op the audit would count.
 */
export function venueDepartmentPatch(
  venue: Pick<VenueDto, 'department'>,
  next: string,
): VenuePatch | null {
  const stored = venue.department ?? null;
  const draft = next === 'OP' || next === 'OS' ? next : null;
  return stored === draft ? null : { department: draft };
}

/**
 * ADDITIVE (v1.9.15, T.Z94) — the venue's OWN department as the card prints it: the
 * full RU name (the owner's 2026-09-14 wording — the ОП/ОС codes stay storage
 * spellings), «Не задано» when the venue carries none. Deliberately NOT
 * `departmentLabelOf` (that one answers «Без отдела», the users-tab wording): a venue
 * with an unset field is not "without a department" — its department is DERIVED from
 * the managers until the field is set, and the label must not claim either state.
 */
export function venueDepartmentLabel(venue: Pick<VenueDto, 'department'>): string {
  const own = venue.department;
  return own === 'OP' || own === 'OS' ? DEPARTMENT_LABEL[own] : 'Не задано';
}

/** The six-key map of a row, or `null` when the roster did not carry one. */
export function rowStatusCounts(row: VenueRow | null | undefined): VenueStatusCountMap | null {
  const counts = row?.appStatusCounts;
  if (!counts || typeof counts !== 'object') return null;
  const filled: Partial<VenueStatusCountMap> = {};
  let seen = 0;
  for (const status of DASHBOARD_STATUSES) {
    const value = counts[status];
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
      filled[status] = Math.round(value);
      seen += value;
    }
  }
  // An all-zero map is still a map — the column prints the absence marker for
  // "no data arrived", not for "this venue has no applications".
  return seen === 0 && Object.keys(filled).length === 0 ? null : ({ ...zeroed(), ...filled } as VenueStatusCountMap);
}

function zeroed(): Record<string, number> {
  return Object.fromEntries(DASHBOARD_STATUSES.map((status) => [status, 0]));
}

/**
 * The slice of the #25.5 user directory the join needs — structural, so plain Node can
 * call {@link managerCell} without dragging `import.meta.env` in.
 */
export interface UserDirectoryLike {
  id: number;
  email: string;
  displayName: string | null;
}

/** What the «Менеджер» cell prints, and what it explains about itself. */
export interface ManagerCell {
  /** The cell text: the display name, the bare id, or the absence marker. */
  text: string;
  /** The tooltip when a name is shown (`№ 7`) — the id stays reachable. */
  title?: string;
}

/**
 * The «Менеджер» cell WITH the directory joined in (T.R6): #6 carries the `AppUser.id`
 * only, and a roster a manager reads must not print «№ 12» where a name will do. The
 * directory is one VIEWER-visible read (#25.5), the same source the card's header uses,
 * so the two screens can never disagree about a name; a directory that failed to load
 * (an older api, a refused request) degrades to the bare id the column always printed.
 *
 * The id is not lost when a name IS shown — it becomes the cell's `title`.
 */
export function managerCell(
  managerUserId: number | null | undefined,
  directory: ReadonlyArray<UserDirectoryLike> = [],
): ManagerCell {
  if (typeof managerUserId !== 'number' || !Number.isFinite(managerUserId) || managerUserId <= 0) {
    return { text: MISSING };
  }
  const text = managerDisplayLabel(managerUserId, directory);
  return text === `№ ${managerUserId}` ? { text } : { text, title: `№ ${managerUserId}` };
}

/** The control status as the roster prints it — free-form curator text. */
export function controlStatusLabel(controlStatus: string | null | undefined): string {
  const text = controlStatus?.trim();
  return text ? text : MISSING;
}

// ---------------------------------------------------------------------------
// Roster columns (T.R9) — the column picker
// ---------------------------------------------------------------------------

/**
 * The roster columns, in table order. The table got wide enough that a curator
 * cannot read it any more, so every column beyond the working set is opt-in and
 * the choice is persisted per browser (`localStorage`, see
 * {@link parseVisibleColumns} / {@link serializeVisibleColumns}).
 *
 * `name` is deliberately NOT in this registry's toggleable set — the venue name
 * is the row's identity and the only way into the card, so it always renders.
 */
export type RosterColumnId =
  | 'address'
  | 'region'
  | 'manager'
  | 'curator'
  | 'programsCount'
  | 'control'
  | 'applications'
  | 'case'
  | 'freed'
  | 'approval'
  | 'comment'
  | 'programsSelection'
  | 'enrolled'
  | 'reportUrl';

export interface RosterColumn {
  id: RosterColumnId;
  /** The header the table prints (RU, the sheet's own words where it has one). */
  label: string;
  /** The working set shown before the curator touches the picker. */
  byDefault: boolean;
  /** A hint the picker prints under the label when the column is not self-evident. */
  hint?: string;
}

export const ROSTER_COLUMNS: ReadonlyArray<RosterColumn> = Object.freeze([
  { id: 'address', label: 'Адрес', byDefault: true },
  { id: 'region', label: 'Регион', byDefault: true },
  { id: 'manager', label: 'Менеджер', byDefault: true },
  { id: 'curator', label: 'Куратор', byDefault: true, hint: 'Придёт с T.R8' },
  { id: 'programsCount', label: 'Кол-во ДОП', byDefault: true, hint: 'Ссылок в реестре' },
  { id: 'control', label: 'Контроль', byDefault: true },
  { id: 'applications', label: 'Заявления', byDefault: true },
  { id: 'case', label: 'Кейс', byDefault: false },
  { id: 'freed', label: 'Высвободились', byDefault: false },
  { id: 'approval', label: 'Согласование', byDefault: false, hint: 'Анна/Катя' },
  { id: 'comment', label: 'Комментарий', byDefault: false },
  { id: 'programsSelection', label: 'Выбор программ', byDefault: false },
  { id: 'enrolled', label: 'Зачислено', byDefault: false },
  { id: 'reportUrl', label: 'Отчет-ссылка', byDefault: false },
]);

export const ROSTER_COLUMN_IDS: ReadonlyArray<RosterColumnId> = ROSTER_COLUMNS.map((column) => column.id);

/** The columns a roster with no stored choice shows. */
export const DEFAULT_ROSTER_COLUMNS: ReadonlyArray<RosterColumnId> = Object.freeze(
  ROSTER_COLUMNS.filter((column) => column.byDefault).map((column) => column.id),
);

/** `localStorage` key the picker persists the selection under. */
export const ROSTER_COLUMNS_STORAGE_KEY = 'cbm.venues.columns';

/**
 * The stored selection → a valid, de-duplicated, table-ordered id list. Anything
 * that is not a stored selection falls back to the defaults; unknown ids (a
 * column a newer build dropped) and a wiped or corrupted value never break the
 * table — the worst case is the default view.
 */
export function parseVisibleColumns(raw: string | null | undefined): ReadonlyArray<RosterColumnId> {
  if (raw === null || raw === undefined) return DEFAULT_ROSTER_COLUMNS;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return DEFAULT_ROSTER_COLUMNS;
  }
  if (!Array.isArray(parsed)) return DEFAULT_ROSTER_COLUMNS;
  const valid = new Set<string>(ROSTER_COLUMN_IDS);
  const seen = new Set<RosterColumnId>();
  for (const value of parsed) {
    if (typeof value !== 'string' || !valid.has(value) || seen.has(value as RosterColumnId)) continue;
    seen.add(value as RosterColumnId);
  }
  return ROSTER_COLUMN_IDS.filter((id) => seen.has(id));
}

/** Inverse of {@link parseVisibleColumns} — the value the picker stores. */
export function serializeVisibleColumns(columns: ReadonlyArray<RosterColumnId>): string {
  return JSON.stringify(ROSTER_COLUMN_IDS.filter((id) => columns.includes(id)));
}

/**
 * The header cells of a roster: the identity column first, then the picked
 * columns in table order. `name` is always first, a picked column never
 * duplicates it, and an empty selection still renders a usable table (just the
 * names).
 */
export function rosterHeaderColumns(visible: ReadonlyArray<RosterColumnId>): ReadonlyArray<RosterColumnId> {
  return ROSTER_COLUMN_IDS.filter((id) => id === 'address' || visible.includes(id));
}

/** A row cell of the sheet columns, printable — `MISSING` for a blank cell. */
export function sheetCellLabel(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return MISSING;
  const text = String(value).trim();
  return text === '' ? MISSING : text;
}

/** The header labels of a column selection, in table order. */
export function rosterColumnLabels(ids: ReadonlyArray<RosterColumnId>): string[] {
  return ids.map((id) => ROSTER_COLUMNS.find((column) => column.id === id)?.label ?? id);
}

/**
 * The #8 PATCH body of the card's header edit: ONLY the keys that differ from
 * the venue as it is on screen (`null` clears), or null when nothing changed —
 * an empty body is a 400 by contract (`minProperties: 1`), so the save button
 * has to know.
 *
 * The manager is NOT here any more (Z3): a venue has held several managers since
 * migration 0011, and the deprecated scalar `managerUserId` folds into a
 * ONE-element set on the api side — a card that shows three of them and saved
 * the scalar would silently drop two. The editor stages the whole set and
 * writes it through {@link managerUserIdsPatchFrom}.
 */
export function venuePatchFrom(
  venue: {
    controlStatus: string | null;
    normalizedAddress: string | null;
    curatorName: string | null;
    curatorPhone: string | null;
    curatorEmail: string | null;
  },
  draft: {
    controlStatus: string;
    normalizedAddress: string;
    curatorName: string;
    curatorPhone: string;
    curatorEmail: string;
  },
): VenuePatch | null {
  const patch: VenuePatch = {};
  const control = draft.controlStatus.trim();
  if (control !== (venue.controlStatus ?? '')) patch.controlStatus = control === '' ? null : control;
  const address = draft.normalizedAddress.trim();
  if (address !== (venue.normalizedAddress ?? '')) patch.normalizedAddress = address === '' ? null : address;
  // T.R8 — the curator block. The e-mail is lowercased here so the field reads the way
  // the api stores it (the same rule `Teacher.email` follows).
  const curatorName = draft.curatorName.trim();
  if (curatorName !== (venue.curatorName ?? '')) patch.curatorName = curatorName === '' ? null : curatorName;
  const curatorPhone = draft.curatorPhone.trim();
  if (curatorPhone !== (venue.curatorPhone ?? '')) patch.curatorPhone = curatorPhone === '' ? null : curatorPhone;
  const curatorEmail = draft.curatorEmail.trim().toLowerCase();
  if (curatorEmail !== (venue.curatorEmail ?? '')) patch.curatorEmail = curatorEmail === '' ? null : curatorEmail;
  return Object.keys(patch).length > 0 ? patch : null;
}

/**
 * The managers the editor starts from (Z3): the whole `managerUserIds` set the
 * api serialises, degrading to the deprecated scalar for an older api — a venue
 * whose every manager but the first would otherwise look unassigned.
 */
export function venueManagerIds(venue: { managerUserIds?: number[]; managerUserId: number | null }): number[] {
  if (venue.managerUserIds !== undefined) return [...venue.managerUserIds];
  return venue.managerUserId === null ? [] : [venue.managerUserId];
}

/**
 * Z3 — the manager half of the #8 body: the staged set as a REPLACE-SET, or
 * `null` when nothing moved. Order-insensitive and deduplicated (the api sorts
 * and dedupes on the way in anyway), so a re-seeded pane never writes a no-op
 * whose only diff is the order the api happened to answer with.
 */
export function managerUserIdsPatchFrom(
  current: ReadonlyArray<number> | undefined,
  draft: ReadonlyArray<number>,
): VenuePatch | null {
  const before = [...new Set(current ?? [])].sort((a, b) => a - b);
  const next = [...new Set(draft)].sort((a, b) => a - b);
  if (next.length === before.length && next.every((id, index) => id === before[index])) return null;
  return { managerUserIds: next };
}

/**
 * Every sheet field the card's «Квоты-лист» section writes. Through v1.4.1 only
 * the three T.R9 columns were #8-accepts and the owner kept a private copy of
 * the sheet to correct the rest; contract v1.5 (wave 6) opens the whole block,
 * so the draft does too. `enrolledCount` is the one integer column — it travels
 * in the draft as its display text and is parsed on the way out.
 *
 * Same arithmetic as {@link venuePatchFrom}: only the keys that differ travel,
 * an emptied field becomes `null` (clear), and "nothing changed" is `null` so
 * the save button can refuse an empty body — a 400 by contract.
 */
export interface QuotaSheetDraft {
  profitableCasesCheck: string;
  curatorReportUrl: string;
  extraApproval: string;
  commentImported: string;
  caseInfo: string;
  freedQuotaNote: string;
  onlineControlStatus: string;
  /** The one integer column of the block, carried as its display text. */
  enrolledCount: string;
}

export type QuotaSheetDraftKey = keyof QuotaSheetDraft;

/** Every draft key #8 accepts as text — `enrolledCount` is the integer exception. */
export const QUOTA_SHEET_TEXT_KEYS = [
  'profitableCasesCheck',
  'curatorReportUrl',
  'extraApproval',
  'commentImported',
  'caseInfo',
  'freedQuotaNote',
  'onlineControlStatus',
] as const satisfies readonly Exclude<QuotaSheetDraftKey, 'enrolledCount'>[];

export type QuotaSheetFields = Pick<VenueDto, QuotaSheetDraftKey>;

/** `enrolledCount` is a number on the wire: non-negative integer, or `null` for «none yet». */
export function parseEnrolledCount(text: string): number | null | 'invalid' {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return 'invalid';
  return Number.parseInt(trimmed, 10);
}

/** `number | null` → the text an input shows; `null` is the empty field. */
export function formatEnrolledCount(value: number | null): string {
  return value === null ? '' : String(value);
}

export function quotaSheetPatchFrom(venue: QuotaSheetFields, draft: QuotaSheetDraft): VenuePatch | { enrolledCount: 'invalid' } | null {
  const patch: VenuePatch = {};
  for (const key of QUOTA_SHEET_TEXT_KEYS) {
    const value = draft[key].trim();
    if (value !== (venue[key] ?? '')) patch[key] = value === '' ? null : value;
  }

  const enrolled = parseEnrolledCount(draft.enrolledCount);
  if (enrolled === 'invalid') return { enrolledCount: 'invalid' };
  if (enrolled !== (venue.enrolledCount ?? null)) patch.enrolledCount = enrolled;

  return Object.keys(patch).length > 0 ? patch : null;
}

/** The draft of the sheet fields, as they are on screen. */
export function quotaSheetDraftFrom(venue: QuotaSheetFields): QuotaSheetDraft {
  return {
    profitableCasesCheck: venue.profitableCasesCheck ?? '',
    curatorReportUrl: venue.curatorReportUrl ?? '',
    extraApproval: venue.extraApproval ?? '',
    commentImported: venue.commentImported ?? '',
    caseInfo: venue.caseInfo ?? '',
    freedQuotaNote: venue.freedQuotaNote ?? '',
    onlineControlStatus: venue.onlineControlStatus ?? '',
    enrolledCount: formatEnrolledCount(venue.enrolledCount),
  };
}

/**
 * The «Квоты-лист» rows the card prints, in the sheet's own column order. RU
 * labels are the sheet's words; `editable` marks the fields #8 accepts (the
 * rest are read-only because the seed owns them — an edit would be overwritten
 * by the next import).
 */
export interface QuotaSheetRow {
  label: string;
  testId: string;
  value: string | number | null;
  /** Set on every field #8 accepts — the whole block since contract v1.5. */
  editable?: QuotaSheetDraftKey;
}

export function quotaSheetRows(venue: VenueDto): QuotaSheetRow[] {
  return [
    { label: 'Проверка выгодных кейсов', testId: 'sheet-profitable', value: venue.profitableCasesCheck, editable: 'profitableCasesCheck' },
    { label: 'Ссылка на отчет куратора', testId: 'sheet-report-url', value: venue.curatorReportUrl, editable: 'curatorReportUrl' },
    { label: 'Дополнительное согласование', testId: 'sheet-extra-approval', value: venue.extraApproval, editable: 'extraApproval' },
    { label: 'Комментарий менеджера', testId: 'sheet-comment', value: venue.commentImported, editable: 'commentImported' },
    { label: 'Кейс (1-6)', testId: 'sheet-case', value: venue.caseInfo, editable: 'caseInfo' },
    // «Выбор программ» lives in its OWN section (T.W3) — see
    // {@link programsSelectionLines}: a quota-priority list a manager reads as a
    // block, not a one-line cell among the sheet's attributes. It is also the one
    // sheet field that stays seed-only: the import re-derives the online quota
    // split from it.
    { label: 'Кол-во зачисленных', testId: 'sheet-enrolled', value: venue.enrolledCount, editable: 'enrolledCount' },
    { label: 'Высвободились квоты', testId: 'sheet-freed', value: venue.freedQuotaNote, editable: 'freedQuotaNote' },
    { label: 'Статусы для онлайн квот', testId: 'sheet-online-status', value: venue.onlineControlStatus, editable: 'onlineControlStatus' },
  ];
}

// ---------------------------------------------------------------------------
// «Выбор программ» (T.W3) — the sheet cell as its own info block
// ---------------------------------------------------------------------------

/** The header hint of the card's «Выбор программ» block — it is not a quota. */
export const PROGRAMS_SELECTION_HINT = 'справочно — не квота';

/** What the block prints when the venue has not filled the cell in. */
export const PROGRAMS_SELECTION_EMPTY_TITLE = 'Площадка ещё выбирает';

/**
 * The raw multi-line cell → the lines the block lists, in the sheet's order.
 * A venue writes its programme priorities as a `\n`-enumerated cell
 * (docs/02 §6: «перечисления через `\n`»), so the block renders one row per
 * line — a line break is the data, not formatting to fold away. Blank lines and
 * the padding around them are sheet noise, dropped.
 */
export function programsSelectionLines(value: string | null | undefined): string[] {
  return (value ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

// ---------------------------------------------------------------------------
// «Факт офлайн по датам» (T.W5) — the per-snapshot offline trend
// ---------------------------------------------------------------------------

/** The section title the card prints. */
export const OFFLINE_TREND_TITLE = 'Факт офлайн по датам';

/** The header hint — what the dates ARE (ЕПГУ cut dates, not a calendar). */
export const OFFLINE_TREND_HINT = 'по срезам ЕПГУ';

/** What the block prints when the api sent no points at all. */
export const OFFLINE_TREND_EMPTY_TITLE = 'Срезов ЕПГУ пока нет';

/**
 * The block keeps the LAST cuts — the trend a manager acts on is the recent
 * one, and a card section has room for a handful of bars, not the whole
 * campaign.
 */
export const OFFLINE_TREND_MAX_BARS = 5;

/** One bar of the mini chart: its RU labels, its count and its scaled height. */
export interface OfflineTrendBar {
  /** `8.09` — the label under the bar (the sheet's own way of writing a date). */
  label: string;
  /** `8.09.2026` — the bar's tooltip, so a two-bar «8.09» never stays ambiguous. */
  title: string;
  count: number;
  /** Bar height as a whole percent of the tallest bar (`0`…`100`). */
  heightPercent: number;
}

/** The slice of `OfflineFactPoint` the model needs — structural, so plain Node can run it. */
export interface OfflineFactPointLike {
  date: string;
  count: number;
}

/** The api always sends the date and a count; a stub must not draw a NaN bar. */
function sanePoint(point: OfflineFactPointLike): OfflineFactPointLike | null {
  const date = typeof point?.date === 'string' ? new Date(point.date) : null;
  if (date === null || Number.isNaN(date.getTime())) return null;
  const count = typeof point.count === 'number' && Number.isFinite(point.count) ? Math.max(Math.round(point.count), 0) : null;
  return count === null ? null : { date: point.date, count };
}

/** `8.09` / `8.09.2026`, UTC getters — the stored instants are UTC and the api's RU dates are too. */
function ruDateParts(iso: string): { day: string; month: string; year: string } {
  const date = new Date(iso);
  return {
    day: String(date.getUTCDate()),
    month: String(date.getUTCMonth() + 1).padStart(2, '0'),
    year: String(date.getUTCFullYear()),
  };
}

/**
 * The wire points → the bars the card draws, oldest on the left, at most
 * {@link OFFLINE_TREND_MAX_BARS} of them.
 *
 * Normalising is defensive by design: the contract keeps the field optional, so
 * an older api (or a hand-written stub) may send nothing, a junk date or a NaN
 * count. Points collapse per calendar DAY with the later one winning — a day
 * with two cuts of the same campaign date must not produce two identical
 * labels — and the heights scale against the tallest bar so a venue with 3
 * applications reads as well as one with 300.
 */
export function offlineTrendBars(
  points: ReadonlyArray<OfflineFactPointLike> | null | undefined,
  limit: number = OFFLINE_TREND_MAX_BARS,
): OfflineTrendBar[] {
  const byDay = new Map<string, { ms: number; count: number }>();
  for (const point of points ?? []) {
    const sane = sanePoint(point);
    if (!sane) continue;
    const ms = new Date(sane.date).getTime();
    // The api answers ascending, so the last write per day is the later cut.
    byDay.set(new Date(sane.date).toISOString().slice(0, 10), { ms, count: sane.count });
  }

  const ordered = [...byDay.values()].sort((a, b) => a.ms - b.ms).slice(-Math.max(limit, 0));
  const tallest = ordered.reduce((max, bar) => Math.max(max, bar.count), 0);

  return ordered.map((bar) => ({
    label: (() => {
      const { day, month } = ruDateParts(new Date(bar.ms).toISOString());
      return `${day}.${month}`;
    })(),
    title: (() => {
      const { day, month, year } = ruDateParts(new Date(bar.ms).toISOString());
      return `${day}.${month}.${year}`;
    })(),
    count: bar.count,
    heightPercent: tallest > 0 ? Math.max(Math.round((bar.count / tallest) * 100), 2) : 2,
  }));
}

/** The curator block reads as three fields; an unfilled one prints an em dash, never «null». */
export function curatorLabel(value: string | null | undefined): string {
  const text = value?.trim();
  return text ? text : MISSING;
}

// ---------------------------------------------------------------------------
// Quota grid (per program × format)
// ---------------------------------------------------------------------------

/** Column order of the grid — the canonical latin formats, frozen by the contract. */
export const QUOTA_FORMATS: ReadonlyArray<VenueCourseFormat> = ['OFFLINE', 'ONLINE'];

export interface QuotaRow {
  courseId: number;
  /** One plan per format; a format the venue has no quota for stays 0. */
  plans: Record<VenueCourseFormat, number>;
  total: number;
}

function sanePlan(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
}

function saneFormat(value: string | null | undefined): VenueCourseFormat | null {
  if (value === 'OFFLINE' || value === 'ONLINE') return value;
  const folded = value?.trim().toUpperCase();
  return folded === 'OFFLINE' || folded === 'ONLINE' ? folded : null;
}

/**
 * `QuotaDto[]` → one row per program, ascending by `courseId`. A plan is SET,
 * not added: `@@unique([venueId, courseId, format])` means the api can only
 * send one row per (program, format), and a duplicate would be a contract bug
 * we surface as "the last one wins" rather than a doubled plan.
 */
export function quotaRows(quotas: ReadonlyArray<QuotaDto> | null | undefined): QuotaRow[] {
  const byCourse = new Map<number, QuotaRow>();
  for (const quota of quotas ?? []) {
    const courseId = quota?.courseId;
    const format = saneFormat(quota?.format);
    if (typeof courseId !== 'number' || !Number.isFinite(courseId) || format === null) continue;
    const row = byCourse.get(courseId) ?? { courseId, plans: { OFFLINE: 0, ONLINE: 0 }, total: 0 };
    row.plans[format] = sanePlan(quota.plan);
    row.total = QUOTA_FORMATS.reduce((sum, key) => sum + row.plans[key], 0);
    byCourse.set(courseId, row);
  }
  return [...byCourse.values()].sort((a, b) => a.courseId - b.courseId);
}

/** Σ plan over every program and format — the venue's seating capacity. */
export function plansTotal(quotas: ReadonlyArray<QuotaDto> | null | undefined): number {
  return (quotas ?? []).reduce((sum, quota) => sum + sanePlan(quota?.plan), 0);
}

/** Σ `appStatusCounts` — the applications the card talks about. */
export function applicationsTotal(counts: Partial<VenueStatusCountMap> | null | undefined): number {
  return sumByStatus(counts);
}

// ---------------------------------------------------------------------------
// Failure copy
// ---------------------------------------------------------------------------

/** The slice of `ApiError` the copy needs — structural, so the model stays importable in plain Node. */
export interface FailureShape {
  status?: number;
  code?: string;
  message?: string;
}

/**
 * T.Z34 — the section speaks about ADDRESSES (the api term stays `aliases`; the
 * ADDRESS kind is the subject), so the cross-venue 409 names the address. A NAME
 * value gets its own sentence: «уникальный» must not read as «два школы —
 * нельзя» (a name collision across regions is real data, the kind just has to
 * say so).
 */
export const ALIAS_CONFLICT_TEXT =
  'Этот адрес уже привязан к другой площадке — адрес должен быть уникальным.';

export const NAME_ALIAS_CONFLICT_TEXT =
  'Такое название уже привязано к другой площадке — оно должно быть уникальным.';

/** The message under an api failure, with the documented code as the authority. */
export function describeFailure(error: FailureShape | null | undefined, fallback: string): string {
  const message = error?.message?.trim();
  return message ? message : fallback;
}

/**
 * What the alias form prints. #9's 409 is the ONE outcome a curator can act on
 * (pick another spelling), so it gets its own sentence; everything else falls
 * back to the api's own message. `kind` (T.Z34) picks the noun — the address
 * sentence is the default, because the section is about addresses.
 */
export function aliasErrorText(error: FailureShape | null | undefined, kind: string = 'ADDRESS'): string | null {
  if (!error) return null;
  if (error.code === 'CONFLICT' || error.status === 409) {
    return kind === 'NAME' ? NAME_ALIAS_CONFLICT_TEXT : ALIAS_CONFLICT_TEXT;
  }
  if (error.code === 'FORBIDDEN' || error.status === 403) {
    return 'Недостаточно прав: адресами площадок управляет администратор.';
  }
  return describeFailure(error, 'Не удалось сохранить адрес.');
}

// ---------------------------------------------------------------------------
// T.Z34 — the «Адреса» uniqueness pre-check (client half)
// ---------------------------------------------------------------------------

/**
 * The PREFIX fold of the shared address normalizer
 * (`packages/shared/src/address/normalize.ts`): NFKC → hyphen family → RU lower
 * case → «ё→е» → whitespace collapse. Deliberately model-local (the unit
 * project cannot import the shared ESM build) and deliberately a PREFIX only:
 *
 *   if two spellings are equal HERE, they are equal under the full fold too
 *   (every later rule is the same deterministic rewrite applied to both sides),
 *   so a pre-check hit is PROOF of a conflict — the form can block the submit.
 *   Inequality here is nothing: «д.37, к.2» vs «д.37 корпус 2» meet only in the
 *   structural block this fold does not run, and for those the server's own
 *   #9 409 is the authority (it applies the exact fold at read time).
 */
export function addressKeyHint(raw: string): string {
  if (typeof raw !== 'string') return '';
  return raw
    .normalize('NFKC')
    .replace(/[­‐‑‒–—―−﹘﹣－]/g, '-')
    .toLocaleLowerCase('ru')
    .replace(/ё/g, 'е')
    .replace(/\s+/g, ' ')
    .trim();
}

/** A directory row the pre-check reads (`VenueDirectoryEntry`, shape-only here). */
export interface VenueAddressDirectoryEntry {
  id: number;
  name: string;
  address: string | null;
}

/** What one pre-check hit says: who already holds the address. */
export interface VenueAddressConflict {
  venueName: string;
  /** True when the holder is the card's own venue — a duplicate of its own spelling. */
  own: boolean;
}

export interface VenueAddressConflictInput {
  kind: string;
  value: string;
  /** The card's venue — the owner of `ownAliases`/`ownVenueAddress`, when known. */
  ownVenueId?: number;
  ownVenueName?: string | null;
  ownVenueAddress?: string | null;
  /** The venue's own dictionary (#9.5) — its ADDRESS rows are keys of THIS venue. */
  ownAliases?: ReadonlyArray<{ kind: string; value: string }>;
  /** The roster directory (#6, `useVenueDirectory`) — the other venues' master addresses. */
  directory?: ReadonlyArray<VenueAddressDirectoryEntry>;
}

/**
 * The address the admin just typed, checked against what the client can already
 * see: the venue's own ADDRESS aliases, its own master address, and the other
 * venues' master addresses from the directory. Returns the FIRST holder, or
 * null. ADDRESS-kind values only — a NAME spelling is never judged against an
 * address, exactly like the server's rule.
 *
 * Best-effort by design: the directory carries master addresses only (there is
 * no bulk read of other venues' aliases), it is scoped to the session's default
 * venue view, and the fold is the proof-only prefix — the api's #9/#9.6 409
 * remains the authoritative refusal for everything the client cannot see.
 */
export function findAddressConflict(input: VenueAddressConflictInput): VenueAddressConflict | null {
  if (input.kind !== 'ADDRESS') return null;
  const key = addressKeyHint(input.value);
  if (key === '') return null;

  const ownAlias = (input.ownAliases ?? []).find((alias) => alias.kind === 'ADDRESS' && addressKeyHint(alias.value) === key);
  if (ownAlias) {
    return { venueName: input.ownVenueName ?? 'эта площадка', own: true };
  }
  if (input.ownVenueAddress && addressKeyHint(input.ownVenueAddress) === key) {
    return { venueName: input.ownVenueName ?? 'эта площадка', own: true };
  }
  const holder = (input.directory ?? []).find(
    (venue) => venue.id !== input.ownVenueId && venue.address !== null && addressKeyHint(venue.address) === key,
  );
  return holder ? { venueName: holder.name, own: false } : null;
}

/** The inline sentence of a pre-check hit («этот адрес уже привязан к площадке …»). */
export function addressConflictHintText(conflict: VenueAddressConflict): string {
  return conflict.own
    ? `Этот адрес уже есть у площадки «${conflict.venueName}» — повторное добавление ничего не меняет.`
    : `Этот адрес уже привязан к площадке «${conflict.venueName}».`;
}

/** The hint the «Обновить связи» button shows once the api answered 202 (#30). */
export const VENUE_MATCH_RUN_QUEUED_TEXT =
  'Пересопоставление запущено — несовпавшие заявления воркер перепроверит сам, результат появится в «Очереди ручного разбора».';

// ---------------------------------------------------------------------------
// T.Z35c — the «Обновить связи» visibility lifecycle
// ---------------------------------------------------------------------------

/**
 * Where the venue's «Обновить связи» offer stands IN THIS PANE SESSION — the
 * card the user has open right now, not the venue's history:
 *
 *   * `idle`    a fresh card. Nothing was touched here yet, so a matcher pass
 *               has nothing new to pick up — the button is HIDDEN. (T.Z34 put
 *               it on every card that held an address, and the owner read that
 *               as «something is wrong with this venue».)
 *   * `changed` a write in THIS session changed the venue's ADDRESSES: the
 *               button + hint SHOW, offering the re-match.
 *   * `queued`  the pass was armed (the api answered 202): the button and its
 *               hint HIDE again — arming it twice changes nothing — and the
 *               «Пересопоставление запущено…» sentence shows instead. A NEXT
 *               address change re-arms the offer (back to `changed`).
 *
 * CLIENT STATE ONLY, on purpose: opening another venue (or reloading) starts
 * the session over at `idle`, and the record of what was armed is the worker's
 * own queue, not the card. The pane remounts per venue (`key={venueId}` in
 * `VenueCard`), which is what makes the reset happen.
 */
export type MatchRunPhase = 'idle' | 'changed' | 'queued';

/**
 * The one render rule of the lifecycle: the offer shows in `changed` and NOWHERE
 * else — not on a fresh card (`idle`), not once the pass is armed (`queued`, the
 * queued sentence takes its place).
 */
export function matchRunOfferVisible(phase: MatchRunPhase): boolean {
  return phase === 'changed';
}

/**
 * Does THIS alias write change the venue's ADDRESS dictionary — the only thing a
 * «Обновить связи» pass can act on? An ADD counts when the row added is an address;
 * an EDIT counts when either side of the move is one (a value rewrite of an address
 * row, or a kind flip NAME→ADDRESS / ADDRESS→NAME — the venue gains or loses an
 * address either way). A NAME add or NAME→NAME edit changes no address, so it must
 * not arm an offer.
 *
 * `before` is the row's kind before the edit; omitted on the add path.
 */
export function aliasWriteTouchesAddress(op: 'add' | 'edit', kind: string, before?: string): boolean {
  return op === 'add' ? kind === 'ADDRESS' : kind === 'ADDRESS' || before === 'ADDRESS';
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export function aliasKindLabel(kind: string): string {
  return kind === 'ADDRESS' ? 'Адрес' : kind === 'NAME' ? 'Название' : kind;
}

export function aliasSourceLabel(source: string): string {
  return source === 'AUTO' ? 'Авто' : source === 'MANUAL' ? 'Вручную' : source;
}

export function formatLabel(format: VenueCourseFormat): string {
  return format === 'ONLINE' ? 'Онлайн' : 'Офлайн';
}
